import React, { forwardRef, useState, useRef, useMemo, useEffect } from 'react';
import PropTypes from 'prop-types';
import MaskedInput from 'react-text-mask';

import InfoTooltip from 'components/InfoTooltip';

const Input = forwardRef(({
  label,
  required = false,
  mask,
  tooltip,
  error,
  autoFocus = false,
  className = '',
  inputClassName = '',
  onFocus: onFocusProp,
  onBlur: onBlurProp,
  ...props
}, ref) => {
  const [isFocused, setIsFocused] = useState(false);
  const internalRef = useRef(null);
  const inputRef = ref || internalRef;

  useEffect(() => {
    if (autoFocus) {
      focusInput();
    }
  }, [autoFocus])

  const focusInput = () => {
    if (!isFocused) {
      if (mask) {
        const inputElement = inputRef?.current?.inputElement || inputRef?.current;
        inputElement?.focus();
      } else {
        inputRef?.current?.focus();
      }
    }
  };

  const handleInputFocus = (e) => {
    setIsFocused(true);
    onFocusProp && onFocusProp(e);
  };

  const handleInputBlur = (e) => {
    setIsFocused(false);
    onBlurProp && onBlurProp(e);
  };

  const baseInputClasses = 'bg-slate-50 text-sm w-full placeholder:text-gray-400 focus:outline-none disabled:cursor-not-allowed';
  const inputClasses = `${baseInputClasses} ${inputClassName}`;

  const wrapperClasses = useMemo(() => {
    const base = 'space-y-2 rounded-lg bg-slate-50 border transition-all duration-200 p-3 cursor-text';

    if (error) {
      return `${base} ring-1 ${isFocused ? 'ring-red-400 border-red-400' : 'ring-red-300 border-red-300'} hover:border-red-400 hover:ring-red-400`;
    }

    if (isFocused) {
      return `${base} border-blue-400 ring-1`;
    }

    return `${base} border-slate-200 hover:border-gray-400`;
  }, [error, isFocused]);

  return (
    <div className={`flex flex-col gap-2 ${className}`}>
      <div
        className={wrapperClasses}
        onClick={focusInput}
      >
        {label && (
          <div className="flex gap-2">
            <label className={`
              block text-xs uppercase font-bold transition-all duration-200
              ${error ? 'text-red-500' : 'text-slate-500'}
            `}
            >
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {tooltip && <InfoTooltip tooltip={tooltip} />}
          </div>
        )}
        {mask ? (
          <MaskedInput
            mask={mask}
            ref={inputRef}
            className={inputClasses}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            render={(maskedInputRef, inputProps) => (
              <input ref={maskedInputRef} {...inputProps} className={inputClasses} />
            )}
            {...props}
          />
        ) : (
          <input
            ref={inputRef}
            className={inputClasses}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            {...props}
          />
        )}
      </div>
      {error && error !== 'The input field is required' && <p className="text-xs text-red-500 px-2">{error}</p>}
    </div>
  );
});

Input.propTypes = {
  label: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  required: PropTypes.bool,
  mask: PropTypes.string,
  tooltip: PropTypes.string,
  error: PropTypes.string,
  className: PropTypes.string,
  inputClassName: PropTypes.string,
  onChange: PropTypes.func,
  autoFocus: PropTypes.bool,
};

Input.defaultProps = {
  placeholder: '',
  required: false,
  mask: null,
  tooltip: null,
  className: '',
  inputClassName: '',
  onChange: null,
  error: null,
  autoFocus: false,
};

export default Input;
